import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Edit, Power, PowerOff, Users, Filter, Trash2, Menu, ExternalLink, GitMerge, Link2 } from 'lucide-react'
import clientesService from '../../services/clientesService'
import { formatCpfCnpj } from '../../utils/masks'
import { useToast } from '../../hooks/useToast'
import { useDebounce } from '../../hooks/useDebounce'
import Toast from '../../components/ui/Toast'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useSidebar } from '../../components/layout/Layout'
import { usePermissao } from '../../hooks/usePermissao'
import { useAuthStore } from '../../stores/authStore'
import MesclarClienteModal from '../../components/clientes/MesclarClienteModal'

export default function ClientesLista() {
  const navigate = useNavigate()
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo } = useOrganizador()
  const { setSidebarOpen } = useSidebar()
  const { podeEditar } = usePermissao()
  const { user } = useAuthStore()
  const isAdmin = user?.permission_level === 'admin' || user?.permission_level === 'super_admin'
  // Mesclar duplicado: admin ou personalizado com acesso completo a clientes
  const podeMesclar = isAdmin || (user?.permission_level === 'personalizado' && podeEditar('clientes'))
  const canEdit = podeEditar('clientes')
  const [showMesclarModal, setShowMesclarModal] = useState(false)
  const [clienteParaMesclar, setClienteParaMesclar] = useState(null)
  const [erroVinculosCliente, setErroVinculosCliente] = useState(null) // { cliente, mensagem }
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 450)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [stats, setStats] = useState({ total: 0, socios: 0, ativos: 0, inativos: 0 })
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    grupo: '',
    cidade: '',
    is_socio: '',
    inativos: ''
  })
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [novosCadastrosPublicos, setNovosCadastrosPublicos] = useState(0)

  const organizadorId = organizadorAtivo?.id
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  const labelPlural = isFluxoCavalo ? 'Haras' : 'Fazendas'
  const labelPluralLower = isFluxoCavalo ? 'haras' : 'fazenda'

  const debouncedCidade = useDebounce(filters.cidade, 450)

  useEffect(() => {
    if (organizadorId) {
      setCurrentPage(1)
      loadClientes(true)
    }
  }, [debouncedSearchTerm, organizadorId, filters.grupo, filters.is_socio, filters.inativos, debouncedCidade])

  useEffect(() => {
    if (organizadorId) {
      loadClientes()
    }
  }, [currentPage])

  useEffect(() => {
    if (organizadorId && organizadorAtivo?.produtos_premium?.includes('link_cadastro')) {
      clientesService.contarNovasSubmissoesCadastroPublico(organizadorId)
        .then(r => setNovosCadastrosPublicos(r.total_novas || 0))
        .catch(() => setNovosCadastrosPublicos(0))
    }
  }, [organizadorId, organizadorAtivo])



  const loadClientes = async (isSearch = false) => {
    if (!organizadorId) {
      setClientes([])
      setLoading(false)
      return
    }

    if (isSearch) {
      setSearching(true)
    } else {
      setLoading(true)
    }

    try {
      const activeFilters = {
        grupo: filters.grupo,
        cidade: debouncedCidade,
        is_socio: filters.is_socio,
        inativos: filters.inativos,
      }
      const res = await clientesService.getByOrganizador(organizadorId, currentPage, 20, debouncedSearchTerm, activeFilters)
      setClientes(res.data || [])
      setTotalPages(res.total_pages || 1)
      setStats({
        total:    res.total_ativos ?? 0,
        socios:   res.total_socios ?? 0,
        ativos:   res.total_ativos ?? 0,
        inativos: res.total_inativos ?? 0,
      })
    } catch (err) {
      console.error('Erro ao carregar clientes:', err)
      error('Erro ao carregar clientes')
      setClientes([])
    } finally {
      setLoading(false)
      setSearching(false)
    }
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleEdit = (cliente) => {
    navigate(`/clientes/${cliente.id}`)
  }

  const handleToggleStatus = async (cliente) => {
    try {
      await clientesService.update(organizadorId, cliente.id, { is_active: !cliente.is_active })
      success(`Cliente ${cliente.is_active ? 'inativado' : 'ativado'} com sucesso!`)
      await loadClientes()
    } catch (err) {
      error('Erro ao alterar status do cliente')
    }
  }

  const handleDelete = (cliente) => {
    setConfirmDelete(cliente)
  }

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return
    const clienteAtual = confirmDelete
    setConfirmDelete(null)

    try {
      await clientesService.delete(organizadorId, clienteAtual.id)
      success('Cliente excluído com sucesso!')
      await loadClientes()
    } catch (err) {
      console.error('Erro ao excluir cliente:', err)
      const detail = err.response?.data?.detail || ''
      const temVinculos = detail.includes('possui') || err.response?.status === 409
      if (podeMesclar && temVinculos) {
        setErroVinculosCliente({ cliente: clienteAtual, mensagem: detail })
      } else {
        error(detail || 'Erro ao excluir cliente. Verifique se não há vínculos.')
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue"></div>
      </div>
    )
  }

  return (
    <>
      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteAction}
        title="Excluir Cliente"
        message={`Tem certeza que deseja excluir o cliente "${confirmDelete?.nome}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Dialog: exclusão bloqueada por vínculos → oferecer mesclagem */}
      {erroVinculosCliente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setErroVinculosCliente(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Trash2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Não é possível excluir</h2>
                <p className="text-red-100 text-xs">Cliente com registros vinculados</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">{erroVinculosCliente.mensagem}</p>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-orange-800 mb-1">Este é um cadastro duplicado?</p>
                <p className="text-xs text-orange-700">
                  Você pode transferir todos os registros de <strong>{erroVinculosCliente.cliente.nome}</strong> para
                  outro cadastro e excluir este em seguida.
                </p>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setErroVinculosCliente(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Não, cancelar
                </button>
                <button
                  onClick={() => {
                    setClienteParaMesclar(erroVinculosCliente.cliente)
                    setErroVinculosCliente(null)
                    setShowMesclarModal(true)
                  }}
                  className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <GitMerge className="w-4 h-4" />
                  Sim, migrar dados
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mesclagem */}
      {podeMesclar && (
        <MesclarClienteModal
          isOpen={showMesclarModal}
          onClose={() => { setShowMesclarModal(false); setClienteParaMesclar(null) }}
          cliente={clienteParaMesclar}
          organizadorId={organizadorId}
          onSuccess={() => {
            setShowMesclarModal(false)
            setClienteParaMesclar(null)
            success('Clientes mesclados com sucesso!')
            loadClientes()
          }}
        />
      )}

      <div className="space-y-5">
          {/* Header com botão hambúrguer para mobile */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center justify-between mb-4 md:hidden">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-lg font-semibold text-white truncate">Clientes</h1>
              <div className="w-10"></div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">Clientes</h1>
                <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">Gerencie os clientes do seu leilão</p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {organizadorAtivo?.produtos_premium?.includes('link_cadastro') && (
                  <>
                    <button
                      onClick={() => navigate('/clientes/cadastros-publicos')}
                      className="relative flex-1 sm:flex-none bg-blue-800/40 hover:bg-blue-800/60 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-medium text-sm sm:text-base"
                      title="Ver cadastros recebidos pelo link público"
                    >
                      <Users size={18} />
                      <span className="hidden sm:inline">Cadastros Recebidos</span>
                      {novosCadastrosPublicos > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {novosCadastrosPublicos > 9 ? '9+' : novosCadastrosPublicos}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => navigate('/configuracoes/cadastro-publico')}
                      className="flex-1 sm:flex-none bg-blue-800/40 hover:bg-blue-800/60 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-medium text-sm sm:text-base"
                      title="Configurar link público de cadastro"
                    >
                      <Link2 size={18} />
                      <span className="hidden sm:inline">Link de Cadastro</span>
                    </button>
                  </>
                )}
                {canEdit && (
                  <button
                    onClick={() => navigate('/clientes/novo')}
                    className="flex-1 sm:flex-none bg-white hover:bg-gray-100 text-blue-600 px-4 sm:px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-medium text-sm sm:text-base"
                  >
                    <Plus size={18} />
                    Novo Cliente
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                  <Users size={16} className="text-blue-600 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Total Clientes</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                  <Users size={16} className="text-purple-600 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Sócios</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{stats.socios}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                  <Power size={16} className="text-green-600 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Ativos</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{stats.ativos}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
                  <PowerOff size={16} className="text-red-600 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Inativos</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{stats.inativos}</p>
                </div>
              </div>
            </div>
          </div>

        {/* Filtros e Busca */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Busca */}
            <div className="flex-1 relative">
              {searching ? (
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              )}
              <input
                type="text"
                placeholder={`Buscar por nome, CPF/CNPJ, email, ${labelPluralLower}...`}
                value={searchTerm}
                onChange={handleSearch}
                className="input-rematei pl-10"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Botão Filtros */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary ${showFilters ? 'bg-gray-100' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
            </button>
          </div>

          {/* Filtros Expandidos */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grupo
                  </label>
                  <select
                    value={filters.grupo}
                    onChange={(e) => setFilters({...filters, grupo: e.target.value})}
                    className="input-rematei"
                  >
                    <option value="">Todos os grupos</option>
                    <option value="comprador">Comprador</option>
                    <option value="vendedor">Vendedor</option>
                    <option value="consignador">Consignador</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    placeholder="Filtrar por cidade"
                    value={filters.cidade}
                    onChange={(e) => setFilters({...filters, cidade: e.target.value})}
                    className="input-rematei"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={filters.is_socio}
                    onChange={(e) => setFilters({...filters, is_socio: e.target.value})}
                    className="input-rematei"
                  >
                    <option value="">Todos</option>
                    <option value="true">Sócios</option>
                    <option value="false">Não sócios</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.inativos}
                    onChange={(e) => setFilters({...filters, inativos: e.target.value})}
                    className="input-rematei"
                  >
                    <option value="">Ativos</option>
                    <option value="true">Inativos</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

          {/* Lista de Clientes */}
          <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-opacity duration-200 ${searching ? 'opacity-60' : 'opacity-100'}`}>
          {clientes.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum cliente encontrado
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'Tente ajustar sua busca' : 'Comece criando seu primeiro cliente'}
              </p>
              {canEdit && (
                <button
                  onClick={() => navigate('/clientes/novo')}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Cliente
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-20">
                        ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/5">
                        Cliente
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden xl:table-cell w-32">
                        CPF/CNPJ
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden xl:table-cell w-36">
                        Contato
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden 2xl:table-cell w-40">
                        {labelPlural}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                        Tipo
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clientes.map((cliente) => (
                      <tr
                        key={cliente.id}
                        className="hover:bg-blue-50/50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
                        onClick={() => navigate(`/clientes/${cliente.id}`)}
                        onMouseDown={(e) => {
                          if (e.button === 1) { e.preventDefault(); window.open(`/clientes/${cliente.id}`, '_blank'); }
                        }}
                      >
                        <td className="px-3 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <a
                            href={`/clientes/${cliente.id}`}
                            onClick={(e) => e.preventDefault()}
                            className="w-8 h-8 bg-blue-100 hover:bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                          >
                            <span className="text-blue-600 font-bold text-xs">#{cliente.id}</span>
                          </a>
                        </td>
                        <td className="px-3 py-4">
                          <div>
                            <div className="text-base font-medium text-gray-900 flex items-center gap-1.5">
                              {cliente.apelido && (
                                <span className="text-gray-500 font-normal">{cliente.apelido} ·</span>
                              )}
                              {cliente.nome}
                              {cliente.origem_pisteiro_remoto && (
                                <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full whitespace-nowrap" title="Comprador informado por um pisteiro remoto (Painel V2)">
                                  🚀 Rematei
                                </span>
                              )}
                              {cliente.origem_cadastro === 'link_publico' && (
                                <span className="text-[10px] font-semibold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full whitespace-nowrap" title="Cadastrado através do link de cadastro público">
                                  🔗 Link Cadastro
                                </span>
                              )}
                              {(() => {
                                const cls = cliente.classificacao_manual || (() => {
                                  const temCpf = !!cliente.cpf_cnpj
                                  const temTel = !!cliente.telefone
                                  const temCid = !!cliente.cidade
                                  const temEnd = !!cliente.endereco
                                  const temFaz = cliente.fazendas?.length > 0
                                  const temBanco = cliente.dados_bancarios?.length > 0
                                  if (temCpf && temTel && temCid && temEnd && temFaz && temBanco) return 'ouro'
                                  if (temCpf && temTel && temCid) return 'prata'
                                  return 'bronze'
                                })()
                                const icons = { bronze: '🥉', prata: '🥈', ouro: '🥇' }
                                return <span className="text-xs" title={`Cadastro ${cls}`}>{icons[cls]}</span>
                              })()}
                            </div>
                            <div className="text-sm text-gray-500">
                              {cliente.cidade && `${cliente.cidade} - ${cliente.estado}`}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 hidden xl:table-cell">
                          {cliente.cpf_cnpj ? formatCpfCnpj(cliente.cpf_cnpj) : '-'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap hidden xl:table-cell">
                          <div className="text-sm text-gray-900 truncate max-w-24">{cliente.telefone || '-'}</div>
                          <div className="text-sm text-gray-500 truncate max-w-24">{cliente.email || '-'}</div>
                        </td>
                        <td className="px-3 py-4 hidden 2xl:table-cell">
                          {cliente.fazendas && cliente.fazendas.length > 0 ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {cliente.fazendas.length} {isFluxoCavalo ? 'haras' : 'fazenda(s)'}
                              </div>
                              <div 
                                className="text-xs text-gray-500 cursor-help max-w-40 truncate" 
                                title={cliente.fazendas.map(f => f.nome).join(', ')}
                              >
                                {cliente.fazendas[0]?.nome}
                                {cliente.fazendas.length > 1 && ` +${cliente.fazendas.length - 1}`}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Nenhuma</span>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            cliente.is_socio 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {cliente.is_socio ? 'Sócio' : 'Não sócio'}
                          </span>
                          {cliente.restrito_compra && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 ml-1" title={cliente.restrito_compra_motivo || 'Restrito'}>
                              ⚠️ Restrito
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            cliente.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {cliente.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end items-center gap-1 sm:gap-2">
                            <button
                              onClick={() => window.open(`/clientes/${cliente.id}`, '_blank')}
                              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                              title="Abrir em nova aba"
                            >
                              <ExternalLink size={14} className="sm:w-4 sm:h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(cliente)}
                              className="p-1.5 sm:p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-all"
                              title="Ver / Editar"
                            >
                              <Edit size={14} className="sm:w-4 sm:h-4" />
                            </button>
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleToggleStatus(cliente)}
                                  className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                                    cliente.is_active
                                      ? 'text-red-600 hover:text-red-800 hover:bg-red-100'
                                      : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                                  }`}
                                  title={cliente.is_active ? 'Inativar' : 'Ativar'}
                                >
                                  {cliente.is_active ? <PowerOff size={14} className="sm:w-4 sm:h-4" /> : <Power size={14} className="sm:w-4 sm:h-4" />}
                                </button>
                                <button
                                  onClick={() => handleDelete(cliente)}
                                  className="p-1.5 sm:p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-all"
                                  title="Excluir"
                                >
                                  <Trash2 size={14} className="sm:w-4 sm:h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Cards */}
              <div className="lg:hidden">
                {clientes.map((cliente) => (
                  <div key={cliente.id} className="p-4 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 flex items-center gap-1.5">
                          {cliente.apelido && (
                            <span className="text-gray-500 font-normal">{cliente.apelido} ·</span>
                          )}
                          {cliente.nome}
                          {cliente.origem_cadastro === 'link_publico' && (
                            <span className="text-[10px] font-semibold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full whitespace-nowrap" title="Cadastrado através do link de cadastro público">
                              🔗 Link Cadastro
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {cliente.cpf_cnpj ? formatCpfCnpj(cliente.cpf_cnpj) : 'Sem CPF/CNPJ'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {cliente.telefone || 'Sem telefone'}
                        </p>
                        {cliente.cidade && (
                          <p className="text-sm text-gray-500">
                            {cliente.cidade} - {cliente.estado}
                          </p>
                        )}
                        {cliente.fazendas && cliente.fazendas.length > 0 && (
                          <p 
                            className="text-xs text-blue-600 mt-1 cursor-help" 
                            title={cliente.fazendas.map(f => f.nome).join(', ')}
                          >
                            {cliente.fazendas.length} {isFluxoCavalo ? 'haras' : 'fazenda(s)'}: {
                              cliente.fazendas.length <= 3 
                                ? cliente.fazendas.map(f => f.nome).join(', ')
                                : `${cliente.fazendas.slice(0, 3).map(f => f.nome).join(', ')} e mais ${cliente.fazendas.length - 3}...`
                            }
                          </p>
                        )}
                        <div className="flex items-center flex-wrap gap-1 mt-2">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {cliente.grupo || 'Sem grupo'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            cliente.is_socio 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {cliente.is_socio ? 'Sócio' : 'Não sócio'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            cliente.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {cliente.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                          {cliente.restrito_compra && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                              ⚠️ Restrito
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(cliente)}
                          className="text-rematei-blue hover:text-blue-700 p-2 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleToggleStatus(cliente)}
                              className={`p-2 rounded ${
                                cliente.is_active
                                  ? 'text-red-600 hover:text-red-700'
                                  : 'text-green-600 hover:text-green-700'
                              }`}
                            >
                              {cliente.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDelete(cliente)}
                              className="text-red-600 hover:text-red-700 p-2 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <div className="text-sm text-gray-500">
                Página <span className="font-medium text-gray-700">{currentPage}</span> de <span className="font-medium text-gray-700">{totalPages}</span>
                {' '}· <span className="font-medium text-gray-700">{stats.total}</span> clientes
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
